import type { Metadata } from "next";

import { SITE_URL } from "@/lib/constants";

export const DEFAULT_OG_IMAGE = "/og-keeps.jpg";
const BRAND_SUFFIX = " | Keeps";

interface PageMetadataOptions {
  /** Title text before the "| Keeps" brand suffix (template mode). */
  title?: string;
  /** Exact string used for the document `<title>` and social cards, bypassing the root template. */
  absoluteTitle?: string;
  description: string;
  /** Route path beginning with "/", e.g. "/wedding-qr-code-photo-sharing" or "/". */
  path: string;
  /** Open Graph / Twitter image path (defaults to the site OG image). */
  ogImage?: string;
  /** Emit a `noindex` robots directive (still followable). */
  noindex?: boolean;
}

/**
 * Build a page's `Metadata` with an absolute canonical plus per-page Open Graph
 * and Twitter cards, all derived from `SITE_URL`. In template mode the document
 * `<title>` relies on the root `"%s | Keeps"` template, while social titles
 * always carry the brand suffix so cards read correctly on their own.
 */
export function pageMetadata({
  title,
  absoluteTitle,
  description,
  path,
  ogImage = DEFAULT_OG_IMAGE,
  noindex,
}: PageMetadataOptions): Metadata {
  const canonical = path === "/" ? `${SITE_URL}/` : `${SITE_URL}${path}`;
  const socialTitle = absoluteTitle ?? `${title ?? "Keeps"}${BRAND_SUFFIX}`;

  return {
    title: absoluteTitle ? { absolute: absoluteTitle } : title,
    description,
    alternates: { canonical },
    ...(noindex ? { robots: { index: false, follow: true } } : {}),
    openGraph: {
      type: "website",
      locale: "en_US",
      siteName: "Keeps",
      url: canonical,
      title: socialTitle,
      description,
      images: [{ url: ogImage, width: 1200, height: 630, alt: socialTitle }],
    },
    twitter: {
      card: "summary_large_image",
      title: socialTitle,
      description,
      images: [ogImage],
    },
  };
}

/**
 * `alternates` block for a page that exists in English and French, with reciprocal
 * `hreflang` links. `selfPath` is the page's own path (its canonical); `x-default`
 * points at the English version.
 */
export function bilingualAlternates(
  selfPath: string,
  enPath: string,
  frPath: string,
): Metadata["alternates"] {
  return {
    canonical: `${SITE_URL}${selfPath}`,
    languages: {
      en: `${SITE_URL}${enPath}`,
      fr: `${SITE_URL}${frPath}`,
      "x-default": `${SITE_URL}${enPath}`,
    },
  };
}

type JsonLd = Record<string, unknown>;

/** `SoftwareApplication` schema for the Keeps iOS app (free to start). */
export function softwareApplicationLd(): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Keeps",
    applicationCategory: "PhotoApplication",
    operatingSystem: "iOS",
    url: SITE_URL,
    description:
      "Keeps is a shared, disposable-style camera app for weddings, parties, trips, and private events. Guests scan a QR code to capture photos into one private gallery that reveals together.",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  };
}

/** `FAQPage` schema built from the same `{q,a}` items a visible `<Faq>` renders. */
export function faqPageLd(items: readonly { q: string; a: string }[]): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };
}

/** `Product` + `AggregateOffer` schema describing per-event pricing tiers. */
export function productOffersLd(opts: {
  name: string;
  description: string;
  lowPrice: string;
  highPrice: string;
  offerCount: number;
}): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: opts.name,
    description: opts.description,
    brand: { "@type": "Brand", name: "Keeps" },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "USD",
      lowPrice: opts.lowPrice,
      highPrice: opts.highPrice,
      offerCount: opts.offerCount,
    },
  };
}

/** `BreadcrumbList` schema from an ordered list of crumbs (path is appended to SITE_URL). */
export function breadcrumbLd(
  crumbs: readonly { name: string; path: string }[],
): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((crumb, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: crumb.name,
      item: crumb.path === "/" ? SITE_URL : `${SITE_URL}${crumb.path}`,
    })),
  };
}
